import {observer} from "mobx-react";
import React from "react";
import {errorMessages} from "../../../helpers/errorMessage";
import {ValidatedValue} from "../../../helpers/validatedValue";
import i18n from "../../../i18n/i18n";
import {Popup} from "../../../common/components/shared/popup/popup";
import Date from "../actionForm/dateAndTime/date";
import "./downloadPopup.scss";
import Validator from "../../../helpers/validator";
import {TimetableService} from "../../services/timetableService";
import {TimetableFileFormat} from "../../models/timetable";
import {DateUtils} from "../../../helpers/dateUtils";
import {Api} from "../../../helpers/api";
import FileSaver from "file-saver";
import RadioGroup from "../../../common/components/shared/radioGroup/radioGroup";
import {RadioModel} from "../../../common/models/radio";
import {HttpError} from "../../../common/components/shared/httpError/httpError";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import Row from "../../../common/components/shared/row/row";
import { Utils } from "../../../helpers/utils";

interface DownloadPopupProps {
    onCancel: () => void;
    defaultFrom?: string;
    defaultTo?: string;
}

interface DownloadPopupState {
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    submitButtonBlocked: boolean;

    fromValue: ValidatedValue<Date | null>;
    toValue: ValidatedValue<Date | null>;
    formatValue: ValidatedValue<TimetableFileFormat>;
}

const dateValidators = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

const correctDateValidators = {
    name: "dateIsCorrect",
    fn: Validator.isCorrectDate,
    errorMessage: () => i18n.t("errorMessages.correctDate")
};

class DownloadPopup extends React.Component<DownloadPopupProps, DownloadPopupState> {
    constructor(props: any) {
        super(props);

        const dateFromIsEarlierThanDateToValidator = {
            name: "dateFromIsEarlierThanDateTo",
            fn: () => Validator.dateFromIsEarlierThanDateTo(this.state.fromValue.value, this.state.toValue.value),
            errorMessage: () => i18n.t("errorMessages.dateFromIsEarlierThanDateTo")
        };

        this.state = {
            response: undefined,
            submitButtonBlocked: false,
            fromValue: new ValidatedValue(this.props.defaultFrom ? DateUtils.parseLocalDate(this.props.defaultFrom) : null, [dateValidators, correctDateValidators, dateFromIsEarlierThanDateToValidator]),
            toValue: new ValidatedValue(this.props.defaultTo ? DateUtils.parseLocalDate(this.props.defaultTo) : null, [dateValidators, correctDateValidators]),
            formatValue: new ValidatedValue(TimetableFileFormat.json)
        };
    }

    render() {
        return <Popup
            open={true}
            className="download-popup"
            header={i18n.t("timetable.actions.download.header")}
            content={this.renderContent()}
            actionText={i18n.t("timetable.actions.download.actionText")}
            action={this.submit.bind(this)}
            cancel={this.props.onCancel}
            testId="download-timetable-popup"
            isWaiting={this.state.submitButtonBlocked}
            httpError={
                <>
                    {!Api.isSuccess(this.state.response) && (
                        <HttpError
                            response={this.state.response}
                            cause={i18n.t("timetable.errors.downloadErrorSuffix")}
                        />
                    )}
                </>
            }
        />;
    }

    private renderContent(): JSX.Element {
        return <>
            <div className="container">
                <Row class="flex-type gap-30">
                    <Date
                        testId={"date-from"} dateLabel={i18n.t("timetable.actions.download.from")}
                        dateValue={this.state.fromValue}/>
                    <Date
                        testId={"date-to"} dateLabel={i18n.t("timetable.actions.download.to")}
                        dateValue={this.state.toValue}/>
                </Row>
                <Row class="flex-type gap-30">
                    <RadioGroup
                        testId="format"
                        items={this.formats}
                        validatedValue={this.state.formatValue}
                        radioGroupClassName={"formats-radio-group"}
                    />
                </Row>
            </div>
        </>;
    }

    private get formats(): RadioModel[] {
        return [
            {value: TimetableFileFormat.json, label: TimetableFileFormat.json.toUpperCase()},
            {value: TimetableFileFormat.xls, label: TimetableFileFormat.xls.toUpperCase()},
        ];
    }

    private mediaType(format: TimetableFileFormat) {
        switch (format) {
            case TimetableFileFormat.json:
                return "application/json";
            case TimetableFileFormat.xls:
                return "application/xls";
        }
    }

    private async executeDownload(from: Date, to: Date, format: TimetableFileFormat) {
        this.setState({response: undefined});

        const response = await TimetableService.download(from, to, format);

        this.setState({response});

        if (response && Api.isSuccess(response) && response.headers) {
            const fileName = response.headers["content-disposition"].split("filename=")[1];
            const blob = new Blob([response.data], {type: this.mediaType(this.state.formatValue.value)});
            FileSaver.saveAs(blob, fileName);
            this.props.onCancel();
        }
    }

    private async submit() {
        this.state.fromValue.validate();
        this.state.toValue.validate();
        this.state.formatValue.validate();

        const valid = this.state.fromValue.isValid && this.state.toValue.isValid && this.state.formatValue.isValid;
        if (valid && this.state.fromValue.value && this.state.toValue.value) {
            this.setState({submitButtonBlocked: true});
            await this.executeDownload(this.state.fromValue.value, this.state.toValue.value, this.state.formatValue.value);
            this.setState({submitButtonBlocked: false});
        }
        Utils.scrollToFirstErrorMessage();
    }
}

export default observer(DownloadPopup);
